import React from 'react';
import { Box, Chip, Paper, Typography, Stack, Divider } from '@mui/material';
import { DSLConfig, Rule, SubRule, Conditions, GatewayAllocation } from '../../types/dynamicRouting';

interface ConfigurationViewerProps {
  config: DSLConfig;
}

const operatorSymbol = (op: string) => {
  switch (op) {
    case 'equals':
      return '=';
    case 'not_equals':
      return '≠';
    case 'in':
      return 'IN';
    case 'not_in':
      return 'NOT IN';
    case 'greater_than':
      return '>';
    case 'less_than':
      return '<';
    case 'greater_than_or_equal':
      return '≥';
    case 'less_than_or_equal':
      return '≤';
    case 'between':
      return 'BETWEEN';
    case 'contains':
      return 'CONTAINS';
    case 'starts_with':
      return 'STARTS WITH';
    case 'ends_with':
      return 'ENDS WITH';
    default:
      return op;
  }
};

const isConditionObject = (value: unknown): value is { operator: string; value: any } => {
  return !!value && typeof value === 'object' && 'operator' in (value as any) && 'value' in (value as any);
};

const ConditionsChips: React.FC<{ conditions?: Conditions }> = ({ conditions }) => {
  if (!conditions) return null;
  const logical = conditions.logicalOperator || 'AND';

  const entries = Object.entries(conditions).filter(([key, val]) => key !== 'logicalOperator' && isConditionObject(val));
  if (entries.length === 0) {
    return (
      <Chip label="No conditions (catch-all)" size="small" color="default" variant="outlined" />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {entries.map(([field, cond], idx) => {
        const c = cond as any;
        const value = Array.isArray(c.value) ? `[${c.value.join(', ')}]` : Array.isArray(c.value) ? JSON.stringify(c.value) : c.value;
        return (
          <React.Fragment key={`${field}-${idx}`}>
            <Chip
              label={`${field} ${operatorSymbol(c.operator)} ${value}`}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: '#e0f0ff',
                borderColor: '#0077cc',
              }}
            />
            {idx < entries.length - 1 && (
              <Chip
                label={logical}
                size="small"
                sx={{ backgroundColor: '#d1ffd1', borderColor: '#00aa00' }}
                variant="outlined"
                color="success"
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

interface GatewayChipsProps {
  distribution?: GatewayAllocation[];
  title?: string;
  nameById: (id: string) => string;
}

const GatewayChips: React.FC<GatewayChipsProps> = ({ distribution, title, nameById }) => {
  if (!distribution || distribution.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      {title && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {distribution.map((gw, idx) => (
          <Chip
            key={`${gw.gatewayId}-${idx}`}
            label={`${nameById(gw.gatewayId)} (${gw.percentage}%)`}
            size="small"
            variant="outlined"
            sx={{ backgroundColor: '#fff0d1', borderColor: '#ffaa00' }}
          />
        ))}
      </Box>
    </Box>
  );
};

const RuleBlock: React.FC<{ rule: Rule | SubRule; level?: number; index?: number; nameById: (id: string) => string }> = ({ rule, level = 0, index, nameById }) => {
  const hasSubRules = Array.isArray(rule.subRules) && rule.subRules.length > 0;
  const title = rule.name || (typeof index === 'number' ? `Rule ${index + 1}` : undefined);

  return (
    <Paper
      elevation={0}
      sx={{
        border: '2px solid #0077cc',
        borderRadius: 2,
        background: 'white',
        p: 2,
        mb: 2,
        ml: level > 0 ? 2 : 0,
        position: 'relative',
      }}
    >
      {title && (
        <Box sx={{ position: 'absolute', top: -10, left: -2, maxWidth: '100%' }}>
          <Chip color="primary" label={title} size="small" sx={{ borderTopLeftRadius: 4 }} />
        </Box>
      )}

      <Stack spacing={1.5} sx={{mt: 1}}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            If
          </Typography>
          <ConditionsChips conditions={rule.conditions} />
        </Box>

        {hasSubRules && (
          <Box sx={{ pl: 2, borderLeft: '2px dashed #ccc' }}>
            <Stack spacing={1.5}>
              {rule.subRules!.map((sr, i) => (
                <RuleBlock key={sr.id || i} rule={sr} level={level + 1} index={i} nameById={nameById} />
              ))}
            </Stack>
          </Box>
        )}

        <Divider />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Then
          </Typography>
          <GatewayChips distribution={rule.gatewayDistribution} title="Gateway Distribution" nameById={nameById} />
          <GatewayChips distribution={rule.defaultGatewayDistribution} title="Default Distribution (if subrules fail)" nameById={nameById} />
        </Box>
      </Stack>
    </Paper>
  );
};

const ConfigurationViewer: React.FC<ConfigurationViewerProps> = ({ config }) => {
  const nameById = (id: string) => config.gateways.find(g => g.id === id)?.name || id;
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {config.name || 'Routing Configuration'}
      </Typography>
      <Stack>
        {config.rules.map((rule, idx) => (
          <RuleBlock key={rule.id || idx} rule={rule} index={idx} nameById={nameById} />
        ))}
      </Stack>
    </Box>
  );
};

export default ConfigurationViewer;
